import { StickerCard } from "@/components/sticker-card/StickerCard.tsx";
import { WorkflowEditPageProps } from "@/pages/workflows/edit/pages/workflow-edit-pages.tsx";
import { useWorkflowActions } from "@/pages/workflows/workflow-actions-hook.ts";
import { useWorkflows } from "@/pages/workflows/workflow-hook.tsx";
import { ApiWorkflowBlockTypes } from "@/types/workflows/workflow.base-enum.types.ts";
import { Button, ButtonLink, FullScreenPage, Stack, Text, Title } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export const WorkflowEditFinalPage = ({ workflow, workflowVersion }: WorkflowEditPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toggleWorkflowIsActive, isWorkflowActivationPending } = useWorkflowActions();

  const { getWorkflowStatusTitle, getWorkflowTriggerDescription } = useWorkflows();

  const getTitleComponent = () => {
    if (workflow.is_active) {
      return (
        <Title
          size={"lg"}
          header={t("workflow:workflow_has_been_updated_title")}
          caption={t("workflow:workflow_has_been_updated_description")}
        />
      );
    }

    return (
      <Title
        size={"lg"}
        header={t("workflow:workflow_is_ready_to_launch_title")}
        caption={t("workflow:workflow_is_ready_to_launch_description")}
      />
    );
  };

  const handleActivateWorkflow = async () => {
    const isSuccess = await toggleWorkflowIsActive({
      workflowId: workflow.id,
      currentWorkflowVersionId: workflow.current_version_id,
      is_active: workflow.is_active,
    });
    if (isSuccess) {
      navigate(`/workflows/${workflow.id}/summary`);
    }
  };

  const getCTAButtons = () => {
    if (workflow.is_active) {
      return (
        <ButtonLink
          type={"submit"}
          size={"lg"}
          variant={"primary"}
          // href={`/workflows/${workflow.id}/view/summary`}
          href={`/workflows/library`}
        >
          {t("common:close")}
        </ButtonLink>
      );
    }
    return (
      <>
        <Button
          type={"submit"}
          size={"lg"}
          isLoading={isWorkflowActivationPending}
          onClick={handleActivateWorkflow}
        >
          {t("workflow:activate_automation")}
        </Button>
        <ButtonLink type={"submit"} size={"lg"} variant={"secondary"} href={"/workflows/library"}>
          {t("workflow:skip_and_activate_later")}
        </ButtonLink>
      </>
    );
  };

  return (
    <FullScreenPage isCenterVertical={true} contentSize={"md"}>
      <Stack direction={"horizontal"} items={"center"}>
        <StickerCard
          title={workflow.title}
          caption={workflow.description || undefined}
          emoji={":green_apple:"}
          status={getWorkflowStatusTitle(workflow.trigger_type, workflow.is_active)}
          contentSlot={
            <>
              <Text className={"text-secondary"}>
                {t("plurals:actions_count", {
                  count: workflowVersion.blocks.filter(
                    (block) => block.block_type === ApiWorkflowBlockTypes.action
                  ).length,
                })}
              </Text>
              <Text className={"text-secondary"}>
                {getWorkflowTriggerDescription(workflowVersion)}
              </Text>
            </>
          }
        />

        <Stack className={"pl-18"} gap={"lg"}>
          {getTitleComponent()}

          <Stack direction={"horizontal"} gap={"sm"}>
            {getCTAButtons()}
          </Stack>
        </Stack>
      </Stack>
    </FullScreenPage>
  );
};
